import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { PreviewRecordListItem } from 'v2/views/List/PreviewRecord/PreviewRecordContext'

import { getUrl } from 'app/UrlService'
import { useBreadcrumbsContext } from 'features/breadcrumbs/hooks/useBreadcrumbsContext'
import { ActionContextMenuHandle } from 'features/views/ListView/Actions/ActionContextMenu'
import { useRecordActionButtons } from 'features/views/ListView/Actions/hooks/useRecordActionButtons'
import { useCardViewContext } from 'features/views/ListView/CardView/useCardViewContext'
import { extractSrcFromCoverImageValue } from 'features/views/ListView/utils'

import useDeepEqualsMemoValue from 'v2/ui/utils/useDeepEqualsMemoValue'

import { useCardViewFooterState } from './useCardViewFooterState'
import { useCardViewTitleState } from './useCardViewTitleState'

type UseCardViewCardStateOptions = {
    record?: RecordDto
    siblingRecords?: RecordDto[]
}

type LeftContentType = 'none' | 'cover' | 'profile'

export function useCardViewCardState(options: UseCardViewCardStateOptions) {
    const { record, siblingRecords } = options

    const {
        object,
        stack,
        labelStyle,
        pendingRecords,
        view,
        requestIncludedFields,
        coverImageField,
        profileImageField,
        cardSize,
        cardStyle,
        onRecordClick,
        titleField,
        eyebrowField,
        subtitleField,
    } = useCardViewContext()

    const recordDetailUrl = useMemo(() => {
        return getUrl(`${object?.url}/view/${record?._sid}`, stack)
    }, [object?.url, record?._sid, stack])
    const to = record ? recordDetailUrl : ''

    const { navigateTo } = useBreadcrumbsContext()

    const onClick = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
            // Only open side peek on regular left click.
            if (e.ctrlKey || e.metaKey || e.button === 1 || !record?._sid) return

            e.preventDefault()

            // Check for any record click overrides.
            switch (onRecordClick) {
                case 'none':
                    return
                case 'new_tab':
                    navigateTo({
                        type: 'detail',
                        recordSid: record._sid,
                        object: object!,
                        openAs: 'tab',
                        stack,
                    })
                    return
                case 'detail':
                    navigateTo({
                        type: 'detail',
                        recordSid: record._sid,
                        object: object!,
                        openAs: 'full',
                        stack,
                    })
                    return
                case 'preview': {
                    const recordListItems: PreviewRecordListItem[] | undefined =
                        siblingRecords?.map((r) => ({
                            recordId: r._sid,
                        }))

                    navigateTo({
                        type: 'detail',
                        recordSid: record._sid,
                        object: object!,
                        openAs: 'preview',
                        stack,
                        previewParams: {
                            partOfRecordList: recordListItems
                                ? {
                                      direction: 'vertical',
                                      items: recordListItems,
                                  }
                                : undefined,
                        },
                    })
                }
            }
        },
        [navigateTo, object, onRecordClick, record?._sid, siblingRecords, stack]
    )

    const { titleAttribute, contentAttributes, recordTitle } = useCardViewTitleState({ record })

    const allActions = useRecordActionButtons({
        record: record!,
        object,
        view,
        showSystemActions: true,
    })
    const actionsMemo = useDeepEqualsMemoValue(allActions.map((a) => a.action))
    const actionsRef = useRef(actionsMemo)
    actionsRef.current = actionsMemo

    const {
        leftAttribute: footerLeftAttribute,
        rightAttribute: footerRightAttribute,
        leftAction: footerLeftButton,
        rightAction: footerRightButton,
    } = useCardViewFooterState({ record })

    const cardRef = useRef<HTMLDivElement>(null)

    const actionContextMenuRef = useRef<ActionContextMenuHandle>(null)

    const onContextMenu = useCallback((e: React.MouseEvent<HTMLElement>) => {
        const card = cardRef.current
        if (!card) return

        // Don't open the context menu if there are no actions enabled.
        const actions = actionsRef.current
        if (!actions.length) return

        const target = e.target as HTMLElement
        const closestInteractive = target.closest('a') as HTMLElement | null
        if (closestInteractive && closestInteractive !== card) {
            // Don't open the context menu if the click was on a link.
            return
        }

        e.preventDefault()
        e.stopPropagation()

        actionContextMenuRef?.current?.openAt(e.clientX, e.clientY)
    }, [])

    const additionalEditFields = useMemo(() => {
        const fields = new Map<string, FieldDto>()

        if (titleField) {
            fields.set(titleField._sid, titleField)
        }

        if (footerLeftAttribute && typeof footerLeftAttribute !== 'string') {
            const field = footerLeftAttribute.field
            fields.set(field._sid, field)
        }

        if (footerRightAttribute && typeof footerRightAttribute !== 'string') {
            const field = footerRightAttribute.field
            fields.set(field._sid, field)
        }

        if (eyebrowField) {
            fields.set(eyebrowField._sid, eyebrowField)
        }

        if (subtitleField) {
            fields.set(subtitleField._sid, subtitleField)
        }

        return Array.from(fields.values())
    }, [titleField, eyebrowField, footerLeftAttribute, footerRightAttribute, subtitleField])

    const isPending = record && pendingRecords.some((r) => r._sid === record._sid)

    const coverImageSrc = extractSrcFromCoverImageValue(record, coverImageField)

    const leftContentType: LeftContentType = !!coverImageField
        ? 'cover'
        : !!profileImageField
          ? 'profile'
          : 'none'

    const innerRef = useRef<HTMLDivElement>(null)

    const [isFullWidth, setIsFullWidth] = useState(false)

    useLayoutEffect(() => {
        const element = innerRef.current
        if (!element) return

        const resizeObserver = new ResizeObserver(() => {
            const computedStyle = window.getComputedStyle(element)

            setIsFullWidth(computedStyle.flexDirection === 'row')
        })
        resizeObserver.observe(element)

        return () => {
            resizeObserver.disconnect()
        }
    }, [])

    const placeholderAttribute = titleAttribute

    const placeholderValue =
        titleAttribute && record ? record[titleAttribute.field.api_name] : undefined

    return useMemo(
        () => ({
            to,
            onClick,
            titleAttribute,
            contentAttributes,
            labelStyle,
            cardRef,
            onContextMenu,
            additionalEditFields,
            actionContextMenuRef,
            isPending,
            actionButtons: actionsMemo,
            includeFields: requestIncludedFields,
            footerLeftButton,
            footerRightButton,
            footerLeftAttribute,
            footerRightAttribute,
            coverImageSrc,
            coverImageField,
            profileImageField,
            cardSize,
            cardStyle,
            leftContentType,
            recordTitle,
            isFullWidth,
            innerRef,
            placeholderAttribute,
            placeholderValue,
        }),
        [
            to,
            onClick,
            titleAttribute,
            contentAttributes,
            labelStyle,
            onContextMenu,
            additionalEditFields,
            isPending,
            actionsMemo,
            requestIncludedFields,
            footerLeftButton,
            footerRightButton,
            footerLeftAttribute,
            footerRightAttribute,
            coverImageSrc,
            coverImageField,
            profileImageField,
            cardSize,
            cardStyle,
            leftContentType,
            recordTitle,
            isFullWidth,
            placeholderAttribute,
            placeholderValue,
        ]
    )
}
